import { useMemo, useState } from 'react';
import moment from 'moment';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';

import { useAdminOrganizationsAuditQuery } from '@/client/api/admin/organizations';
import type {
  AdminAuditAction,
  AdminAuditEntry,
} from '@/client/types/admin/users-orgs';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

/** Org shell + role + member actions from the admin audit vocabulary. */
const ORG_AUDIT_ACTIONS: AdminAuditAction[] = [
  'organization.profile.update',
  'organization.hold.set',
  'organization.hold.lift',
  'organization.review.approve',
  'organization.review.reject',
  'organization.searchable.set',
  'organization.vat.set',
  'organization.ownership.transfer',
  'organization.role.create',
  'organization.role.update',
  'organization.role.delete',
  'organization.role.permissions_set',
  'organization.role.permission_grant',
  'organization.role.permission_revoke',
  'organization.member.add',
  'organization.member.activate',
  'organization.member.deactivate',
  'organization.member.delete',
  'organization.member.undelete',
  'organization.member.onboard',
  'organization.member.role_assign',
  'organization.member.role_remove',
  'organization.invite.send',
  'organization.invite.cancel',
  'organization.invite.role_update',
];

const TARGET_TYPES = [
  'organization',
  'organization_member',
  'organization_role',
  'all',
] as const;

const PAGE_SIZE = 50;

const MetaCell = ({ meta }: { meta: AdminAuditEntry['meta'] }) => {
  const [open, setOpen] = useState(false);

  if (!meta || Object.keys(meta).length === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {Object.keys(meta).length} field
        {Object.keys(meta).length === 1 ? '' : 's'}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 max-w-md overflow-x-auto rounded-md border bg-gray-50 p-2 font-mono text-xs text-gray-700">
          {JSON.stringify(meta, null, 2)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ActorCell = ({
  actor,
  authid,
}: {
  actor: AdminAuditEntry['actor'];
  authid: string;
}) => {
  if (!actor) {
    return (
      <span className="break-all font-mono text-xs text-gray-500">
        {authid || '—'}
      </span>
    );
  }

  const name =
    [actor.first_name, actor.last_name].filter(Boolean).join(' ') ||
    (actor.uaid != null ? `uaid ${actor.uaid}` : '—');

  return (
    <div>
      <p className="truncate text-sm font-medium text-gray-900">{name}</p>
      {actor.email && (
        <p className="truncate text-xs text-gray-500">{actor.email}</p>
      )}
    </div>
  );
};

const OrgAuditLogPage = () => {
  useBreadcrumbs([
    { label: 'Organizations', href: '/admin/organizations' },
    { label: 'Audit Log' },
  ]);

  const { filters, handleFilterChange, page, handlePageChange } =
    useListFilters<{
      action: string;
      target_type: string;
      target_id: string;
      actor_authid: string;
      outcome: string;
    }>({
      initialFilters: {
        action: '',
        target_type: '',
        target_id: '',
        actor_authid: '',
        outcome: '',
      },
      initialLimit: PAGE_SIZE,
    });

  const action = filters.action;
  const targetType = filters.target_type;
  const targetId = filters.target_id;
  const actorAuthid = filters.actor_authid;
  const outcome = filters.outcome;

  const [actorQuery, setActorQuery] = useState('');
  const skip = (page - 1) * PAGE_SIZE;

  const params = useMemo(() => {
    const next: {
      action?: string;
      target_type?: string;
      target_id?: string;
      actor_authid?: string;
      outcome?: string;
      limit: number;
      skip: number;
    } = { limit: PAGE_SIZE, skip };
    if (action) next.action = action;
    if (targetType && targetType !== 'all') next.target_type = targetType;
    if (targetId) next.target_id = targetId;
    if (actorAuthid) next.actor_authid = actorAuthid;
    if (outcome) next.outcome = outcome;
    return next;
  }, [action, targetType, targetId, actorAuthid, outcome, skip]);

  const { data, isLoading, isFetching } = useAdminOrganizationsAuditQuery(params);
  const entries = data?.actions ?? [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <ScrollText className="h-6 w-6 text-blue-600" />
            Organizations audit log
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Admin actions on organizations, roles and memberships, most recent
            first.
          </p>
        </div>

        <div className="mb-4 rounded-lg bg-white p-4 shadow">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <div className="space-y-1">
              <Label htmlFor="org-audit-action">Action</Label>
              <select
                id="org-audit-action"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">All actions</option>
                {ORG_AUDIT_ACTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="org-audit-target-type">Target type</Label>
              <select
                id="org-audit-target-type"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={targetType}
                onChange={(e) =>
                  handleFilterChange('target_type', e.target.value)
                }
              >
                <option value="">All target types</option>
                {TARGET_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="org-audit-target-id">Target ID</Label>
              <Input
                id="org-audit-target-id"
                placeholder="e.g. oid / ourid / uid"
                value={targetId}
                onChange={(e) =>
                  handleFilterChange('target_id', e.target.value)
                }
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="org-audit-outcome">Outcome</Label>
              <Input
                id="org-audit-outcome"
                placeholder="e.g. success"
                value={outcome}
                onChange={(e) => handleFilterChange('outcome', e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="org-audit-actor">Actor</Label>
              <UserSearchSelect
                value={actorQuery}
                onChange={(value) => {
                  setActorQuery(value);
                  if (actorAuthid) handleFilterChange('actor_authid', '');
                }}
                onSelect={(user) => {
                  setActorQuery(
                    `${user.first_name} ${user.last_name}`.trim()
                  );
                  handleFilterChange('actor_authid', user.authid);
                }}
                placeholder="Search for admin"
              />
              {actorAuthid && (
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setActorQuery('');
                    handleFilterChange('actor_authid', '');
                  }}
                >
                  Clear actor filter
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Timestamp', 'Action', 'Target', 'Outcome', 'Actor', 'Meta'].map(
                    (heading) => (
                      <th
                        key={heading}
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading &&
                  Array.from({ length: 10 }).map((_, index) => (
                    <tr key={index}>
                      {[1, 2, 3, 4, 5, 6].map((cell) => (
                        <td key={cell} className="px-6 py-4">
                          <Skeleton className="h-4 w-28" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!isLoading && entries.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-gray-400"
                    >
                      No audit entries match the current filters.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  entries.map((entry, index) => (
                    <tr
                      key={`${entry.timestamp}-${entry.action}-${index}`}
                      className="align-top hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {moment(entry.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <code className="font-mono text-xs text-gray-900">
                          {entry.action}
                        </code>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        <span className="text-gray-500">
                          {entry.target_type}
                        </span>
                        <span className="text-gray-400"> · </span>
                        <span className="font-mono text-xs">
                          {String(entry.target_id ?? '—')}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {entry.outcome || '—'}
                      </td>
                      <td className="max-w-[220px] px-6 py-4">
                        <ActorCell
                          actor={entry.actor}
                          authid={entry.actor_authid ?? ''}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <MetaCell meta={entry.meta} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6">
            <p className="text-sm text-gray-500">
              Showing {entries.length === 0 ? 0 : skip + 1}–
              {skip + entries.length}
              {isFetching && !isLoading && ' · refreshing…'}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0 || isFetching}
                onClick={() => handlePageChange(Math.max(1, page - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={entries.length < PAGE_SIZE || isFetching}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgAuditLogPage;
